import Button from '@jetbrains/ring-ui/components/button/button'
import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import Select from '@jetbrains/ring-ui/components/select/select'
import plur from 'plur'
import {memo, useCallback, useMemo, useState} from 'react'
import {VictoryPie} from 'victory'
import type {VictoryStyleInterface} from 'victory-core'

import {useBranch} from '../../../../hooks/routes'
import {useIsAutodetectTimeZone} from '../../../../hooks/useIsAutodetectTimeZone'
import {restApi} from '../../../../services/rest'
import cssVariables from '../../../../utils/cssVariables'
import {emptyArray} from '../../../../utils/empty'
import {resolveHelpURL} from '../../../../utils/url'
import {UserProperties} from '../../../../utils/userProperties'
import ContentPanel from '../../../common/ContentPanel/ContentPanel'
import IconButton from '../../../common/IconButton/IconButton'
import Link from '../../../common/Link/Link'
import {useSequenceLoaderReadyHandler} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import {useTestOccurencesArg} from '../Tests.rest'
import {makeGetTestOccurrencesList} from '../Tests.selectors'
import {TestStatus} from '../Tests.types'

import Chart from './Chart/Chart'
import type {Props, ContainerProps} from './TestHistoryChart.types'
import {
  getAverageData,
  getData,
  getDownloadLink,
  getLocator,
  getTestRunsStatistic,
} from './TestHistoryChart.utils'

import styles from './TestHistoryChart.css'

import {useAppSelector} from 'src/hooks/react-redux'

const HUNDRED_PERCENT = 100

function Nothing() {
  return null
}

const successRateChartStyles: VictoryStyleInterface = {
  data: {
    fill: ({datum}) => datum.color,
  },
}

function TestHistoryChart({
  className,
  testId,
  projectId,
  buildTypeId,
  testOccurrences,
  expandable,
  width,
}: Props) {
  const branch = useBranch()
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  const {success, failure, ignored} = useMemo(
    () => getTestRunsStatistic(testOccurrences),
    [testOccurrences],
  )
  const testRunsCount = success + failure
  const allTestRunsCount = success + failure + ignored
  const successRate = testRunsCount > 0 ? (success / testRunsCount) * HUNDRED_PERCENT : null
  const successRateChartDate = useMemo(
    () => [
      {
        x: 1,
        y: success,
        color: cssVariables['--ring-icon-success-color'],
      },
      {
        x: 2,
        y: failure,
        color: cssVariables['--ring-icon-error-color'],
      },
    ],
    [failure, success],
  )
  const agentSelectItems = useMemo(
    () =>
      testOccurrences
        .map(testOccurrence => testOccurrence.build?.agent?.name ?? '')
        .filter(Boolean)
        .filter((item, index, array) => array.indexOf(item) === index)
        .sort()
        .map(agentName => ({
          label: agentName,
          key: agentName,
        })),
    [testOccurrences],
  )
  const [agents, setAgents] = useState(agentSelectItems)
  const [showAverage, setShowAverage] = useState(false)
  const [showFailed, setShowFailed] = useState(true)
  const [showPersonalBuilds, setShowPersonalBuilds] = useState(true)
  const [adaptScale, setAdaptScale] = useState(false)
  const handleSelectAgent = useCallback(
    (selectAgentItem: SelectItem<{key: string; label: string}>[]) => {
      setAgents(selectAgentItem)
    },
    [setAgents],
  )
  const handleShowAverage = useCallback(
    () => setShowAverage(!showAverage),
    [showAverage, setShowAverage],
  )
  const handleShowFailed = useCallback(
    () => setShowFailed(!showFailed),
    [showFailed, setShowFailed],
  )
  const handleShowPersonalBuilds = useCallback(
    () => setShowPersonalBuilds(!showPersonalBuilds),
    [showPersonalBuilds, setShowPersonalBuilds],
  )
  const handleAdaptScale = useCallback(
    () => setAdaptScale(!adaptScale),
    [adaptScale, setAdaptScale],
  )
  const hasTestsInPersonalBuilds = useMemo(
    () => Boolean(testOccurrences.find(testOccurrence => testOccurrence.build?.personal)),
    [testOccurrences],
  )
  const hasFailedTests = useMemo(
    () =>
      Boolean(testOccurrences.find(testOccurrence => testOccurrence.status === TestStatus.FAILURE)),
    [testOccurrences],
  )
  const data = useMemo(() => {
    const options = {
      agents,
      showFailed,
      showPersonalBuilds,
    }
    return showAverage
      ? getAverageData(testOccurrences, options, isAutodetectTimeZone)
      : getData(testOccurrences, options)
  }, [agents, showFailed, showPersonalBuilds, showAverage, testOccurrences, isAutodetectTimeZone])
  const getChartKey = useCallback(
    () =>
      `chart-key-${width}-${agents.length}-${
        data.length
      }-${showFailed.toString()}-${showPersonalBuilds.toString()}-${showAverage.toString()}`,
    [data.length, width, agents, showFailed, showPersonalBuilds, showAverage],
  )
  const actions = useMemo(
    () => (
      <div className={styles.actions}>
        <Checkbox
          className={styles.adaptScale}
          label="Adapt scale"
          checked={adaptScale}
          onChange={handleAdaptScale}
        />
        <div className={styles.filter}>
          <div className={styles.showAverage}>
            <Checkbox label="Average" checked={showAverage} onChange={handleShowAverage} />
          </div>
          {hasTestsInPersonalBuilds && (
            <div className={styles.showPersonalBuildsCheckbox}>
              <Checkbox
                label="Show personal"
                checked={showPersonalBuilds}
                onChange={handleShowPersonalBuilds}
              />
            </div>
          )}
          {hasFailedTests && (
            <div className={styles.showFailedCheckbox}>
              <Checkbox label="Show failed" checked={showFailed} onChange={handleShowFailed} />
            </div>
          )}
          <Select
            popupClassName={styles.agentFilterPopup}
            filter
            clear
            type={Select.Type.CUSTOM}
            multiple={{
              selectAll: true,
            }}
            selected={agents}
            data={agentSelectItems}
            onChange={handleSelectAgent}
            customAnchor={({wrapperProps, buttonProps, popup}) => (
              <span {...wrapperProps}>
                <Button {...buttonProps} dropdown>
                  {agentSelectItems.length === agents.length
                    ? 'All agents'
                    : `Selected ${agents.length} ${plur('agent', agents.length)}`}
                </Button>
                {popup}
              </span>
            )}
          />
        </div>
      </div>
    ),
    [
      adaptScale,
      handleAdaptScale,
      showAverage,
      handleShowAverage,
      hasTestsInPersonalBuilds,
      showPersonalBuilds,
      handleShowPersonalBuilds,
      hasFailedTests,
      showFailed,
      handleShowFailed,
      agents,
      agentSelectItems,
      handleSelectAgent,
    ],
  )
  const renderHeading = useMemo(
    () => (
      <div className={styles.heading}>
        {successRate != null && (
          <svg className={styles.headingPieChart} width={16} height={16}>
            <VictoryPie
              standalone={false}
              width={16}
              height={16}
              innerRadius={5}
              padding={0}
              labels={Nothing}
              style={successRateChartStyles}
              data={successRateChartDate}
            />
          </svg>
        )}
        <span>{`Success Rate: ${
          successRate != null ? `${successRate.toFixed(1)}%` : '--'
        } (Last ${allTestRunsCount} Runs)`}</span>
      </div>
    ),
    [allTestRunsCount, successRate, successRateChartDate],
  )
  const renderSubheading = useMemo(
    () => (
      <div className={styles.statisticCounts}>
        {failure > 0 && (
          <div className={styles.statisticFailure}>
            <div className={styles.statisticFailureIcon} />
            {`${failure} failed`}
          </div>
        )}
        {success > 0 && <div>{`${success} successful`}</div>}
        {ignored > 0 && <div>{`${ignored} ignored`}</div>}
      </div>
    ),
    [success, failure, ignored],
  )
  const renderSnippet = useMemo(
    () => (
      <div className={styles.snippet}>
        <IconButton
          className={styles.download}
          href={getDownloadLink(projectId, buildTypeId, testId, branch)}
          title="Download data as CSV"
          icon="download"
          rel="noreferrer"
          target="_blank"
        >
          {'Download'}
        </IconButton>
        <Link
          className={styles.help}
          href={resolveHelpURL('?Statistic%20Charts#StatisticCharts-TestDuration')}
          title="View help"
        >
          {'Help'}
        </Link>
      </div>
    ),
    [branch, buildTypeId, projectId, testId],
  )
  const renderContent = useMemo(
    () => (
      <>
        {data.length > 0 ? (
          <Chart key={getChartKey()} data={data} width={width} withAdaptScale={adaptScale} />
        ) : null}
        {actions}
      </>
    ),
    [actions, adaptScale, data, getChartKey, width],
  )
  return (
    <ContentPanel
      className={className}
      panelType={UserProperties.EXPAND_TEST_HISTORY_CHART_SECTION}
      heading={renderHeading}
      subheading={renderSubheading}
      headerSnippet={renderSnippet}
      content={renderContent}
      expandable={expandable}
      withBorder={false}
    />
  )
}

function TestHistoryChartContainer(props: ContainerProps) {
  const branch = useBranch()
  const locator = getLocator(props.testId, props.projectId, props.buildTypeId, branch)

  const arg = useTestOccurencesArg(locator)
  const {ids, ready} = restApi.endpoints.getAllTestOccurrencesNormalized.useQuery(arg, {
    selectFromResult: ({data}) => ({
      ids: data?.data.result ?? emptyArray,
      ready: data != null,
    }),
  })

  const getTestOccurrencesList = useMemo(makeGetTestOccurrencesList, [])
  const testOccurrences = useAppSelector(state => getTestOccurrencesList(state, ids))

  useSequenceLoaderReadyHandler(ready)

  return testOccurrences.length > 1 ? (
    <TestHistoryChart testOccurrences={testOccurrences} {...props} />
  ) : null
}

export default memo<ContainerProps>(TestHistoryChartContainer)
